// Generated by tutorials/record.cjs from tutorials/articles/h66-printing-setup.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h66 --text-only` (wording) or
// `node tutorials/record.cjs h66` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'printing-setup',
  catalogId: 'h66',
  title: 'Setting up printing and reports',
  summary: 'Getting Started\'s Printing and reports step, part by part: the reports you print every day, how tray tickets, posting menus and labels look, which printer prints what, and finishing the step. A sample printout shows each choice as you make it.',
  audience: 'Administrators setting up a facility that is new to Dietech Online',
  area: 'setup',
  learn: [
    'Pin the reports your kitchen prints every day',
    'Choose the tray ticket paper, what prints on a ticket and the order tickets print in, with a sample beside you',
    'Set up posting menus and nourishment and special request labels',
    'Pick the printer for tray tickets and labels, and decide how tickets print each day',
    'Finish the step, and where to change these settings later',
  ],
  keywords: [
    'printing and reports',
    'printing setup',
    'print setup',
    'set up printing',
    'tray ticket format',
    'tray ticket paper',
    'what prints on a ticket',
    'ticket order',
    'posting menu',
    'posting menus',
    'labels',
    'nourishment labels',
    'special request labels',
    'print preview',
    'sample printout',
    'daily reports',
    'favorites',
    'printer',
    'print agent',
    'test ticket',
    'scheduled reports',
    'getting started',
    'iddsi on tickets',
  ],
  steps: [
    {
      id: 'open',
      part: 'Your daily reports',
      heading: 'Open Printing and reports',
      text: 'In Getting Started, select Printing and reports (step 10) in the list on the left. It needs Facility basics finished first, because the posting menus use your meal names and times.\n\nThe step has eight parts, listed across the top. Each part saved gets a check mark, and the step opens on the first part not saved yet.',
      media: {
        image: {
          src: '/help/printing-setup/open.webp',
          width: 1280,
          height: 448,
        },
        clip: {
          src: '/help/printing-setup/open.mp4',
          width: 1280,
          height: 448,
          durationSec: 2,
        },
        alt: 'Getting Started with Printing and reports open: its eight parts across the top, from 1 Daily reports to 8 Scheduled reports, with Daily reports open.',
      },
    },
    {
      id: 'daily',
      part: 'Your daily reports',
      heading: '1. Daily reports',
      text: 'Tick the reports your kitchen prints every day, such as Tray tickets, Production tally, Posting menu (daily) and Nourishments. They are pinned as your favorites at the top of the Reports page, so they are one click away.\n\nFavorites belong to each person. Everyone else can pin their own from the Reports page.',
      media: {
        image: {
          src: '/help/printing-setup/daily.webp',
          width: 1122,
          height: 670,
        },
        clip: {
          src: '/help/printing-setup/daily.mp4',
          width: 1122,
          height: 670,
          durationSec: 6,
        },
        alt: 'Part 1, Daily reports: Tray tickets, Production tally, Posting menu (daily) and Nourishments and nourishment labels ticked under Reports we print every day.',
      },
    },
    {
      id: 'save-continue',
      part: 'Your daily reports',
      heading: 'Save and continue',
      text: 'Select Save and continue. What you chose is saved, part 1 gets a check mark, and part 2 opens.\n\nCancel puts back what was there when you opened the part. Save and come back later saves and takes you back to your dashboard.',
      media: {
        image: {
          src: '/help/printing-setup/save-continue.webp',
          width: 1280,
          height: 146,
        },
        clip: {
          src: '/help/printing-setup/save-continue.mp4',
          width: 1280,
          height: 146,
          durationSec: 3.1,
        },
        alt: 'The parts across the top with a check mark on Daily reports and Tray ticket format open.',
      },
    },
    {
      id: 'format',
      part: 'How tray tickets look',
      heading: '2. Tray ticket format',
      text: 'Pick the tray ticket paper: 4 Part, 6 Part or 3 Part sheets, 1 Part continuous, or 1 Part 4"x4" for a label printer. The Preview on the right changes as you pick: here a 3 Part sheet, with Letter or Legal paper.\n\nTickets to print says whether a duplicate ticket goes with the tray, for the dining room or the unit.',
      media: {
        image: {
          src: '/help/printing-setup/format.webp',
          width: 1280,
          height: 622,
        },
        clip: {
          src: '/help/printing-setup/format.mp4',
          width: 1280,
          height: 622,
          durationSec: 3.3,
        },
        alt: 'Part 2, Tray ticket format: 3 Part picked, Paper size Letter, Tickets to print Normal and Duplicate, and the Preview of a 3 part sheet with three sample tickets.',
      },
    },
    {
      id: 'content',
      part: 'How tray tickets look',
      heading: '3. What prints on a ticket',
      text: 'The diet order, textures, food allergies and the meal\'s items always print. Choose the rest: how the name prints, date of birth or ID, and what else to print, such as IDDSI levels, feeding status, likes, item sizes, a barcode, or Happy Birthday on the birthday.\n\nDislikes can be only the ones on that meal\'s menu, or all of the resident\'s dislikes. Watch the sample ticket change as you tick.',
      media: {
        image: {
          src: '/help/printing-setup/content.webp',
          width: 1280,
          height: 622,
        },
        clip: {
          src: '/help/printing-setup/content.mp4',
          width: 1280,
          height: 622,
          durationSec: 6,
        },
        alt: 'Part 3, What prints on a ticket: Name Last Name, First Name, IDDSI levels and Feeding status ticked, and the sample tickets showing the IDDSI codes and FS lines.',
      },
    },
    {
      id: 'order',
      part: 'How tray tickets look',
      heading: '4. Ticket order',
      text: 'Sort tickets by name, room number or tray line, so they come off the printer in the order trays are made. For tray line order you can print a separator page between carts.\n\nItems on a ticket print in your menu category order; Change the item order opens it.',
      media: {
        image: {
          src: '/help/printing-setup/order.webp',
          width: 1280,
          height: 504,
        },
        clip: {
          src: '/help/printing-setup/order.mp4',
          width: 1280,
          height: 504,
          durationSec: 5.5,
        },
        alt: 'Part 4, Ticket order: Sort tickets by Tray Line, Print a separator page between carts ticked, and the sample print order cart by cart.',
      },
    },
    {
      id: 'posting',
      part: 'Posting menus and labels',
      heading: '5. Posting menus',
      text: 'Choose how the daily menu names each meal (its name, its name and time, or the time), whether posting menus list the nourishment times, the font sizes and the paper size.\n\nThe Preview shows a daily and a general posting menu with your own meal names and times.',
      media: {
        image: {
          src: '/help/printing-setup/posting.webp',
          width: 1280,
          height: 622,
        },
        clip: {
          src: '/help/printing-setup/posting.mp4',
          width: 1280,
          height: 622,
          durationSec: 5.5,
        },
        alt: 'Part 5, Posting menus: Meal headings Meal Name - Time and the nourishment times ticked, with the Preview of a daily posting menu headed Lunch - 12:00 PM.',
      },
    },
    {
      id: 'labels',
      part: 'Posting menus and labels',
      heading: '6. Labels',
      text: 'Nourishment labels and special request labels each have their label stock, how the name prints, and what else to print: the diet order or the feeding status, and food allergies.\n\nTick Ask which label to start on if you reuse part-used label sheets.',
      media: {
        image: {
          src: '/help/printing-setup/labels.webp',
          width: 1280,
          height: 622,
        },
        clip: {
          src: '/help/printing-setup/labels.mp4',
          width: 1280,
          height: 622,
          durationSec: 4,
        },
        alt: 'Part 6, Labels: Nourishment labels on Avery 5162 with Diet Order and Food allergies picked, and the Preview of a sheet of sample nourishment labels.',
      },
    },
    {
      id: 'printers',
      part: 'Printers, and finishing the step',
      heading: '7. Printers',
      text: 'Print agent shows the kitchen computer that sends reports to your printers, and whether it is online.\n\nUnder Which printer prints what, pick the printer for Tray tickets (it must have one), and for the Production tally and Labels if you like. The list shows the printers the print agent found.',
      media: {
        image: {
          src: '/help/printing-setup/printers.webp',
          width: 1122,
          height: 670,
        },
        clip: {
          src: '/help/printing-setup/printers.mp4',
          width: 1122,
          height: 670,
          durationSec: 6,
        },
        alt: 'Part 7, Printers: the Kitchen Office print agent Online, and Tray tickets set to Kitchen Tickets and Labels to Label Printer.',
      },
    },
    {
      id: 'how-print',
      part: 'Printers, and finishing the step',
      heading: 'How tickets print each day',
      text: 'Say how tray tickets print each day: someone prints them from Reports, or on a schedule at set times. On a schedule shows a link to Print Schedules, where you set the times.\n\nOnce you have saved, Print a test ticket opens Tray Tickets for today with the meal you pick; select Queue there to print it.',
      media: {
        image: {
          src: '/help/printing-setup/how-print.webp',
          width: 1122,
          height: 670,
        },
        clip: {
          src: '/help/printing-setup/how-print.mp4',
          width: 1122,
          height: 670,
          durationSec: 2.6,
        },
        alt: 'How do tray tickets print each day? with On a schedule, at set times picked, the link Set the print times on Print Schedules, and Test ticket with the Meal and Print a test ticket.',
      },
    },
    {
      id: 'scheduled',
      part: 'Printers, and finishing the step',
      heading: '8. Scheduled reports',
      text: 'This part is optional. A scheduled report runs by itself at the times you set and emails the people you pick. The email carries no resident details: it links to the Print Queue, and staff sign in to open the report.\n\nSet up scheduled reports opens the Scheduled Reports page.',
      media: {
        image: {
          src: '/help/printing-setup/scheduled.webp',
          width: 1122,
          height: 268,
        },
        alt: 'Part 8, Scheduled reports: what a scheduled report does and sends, and Set up scheduled reports.',
      },
    },
    {
      id: 'finish',
      part: 'Printers, and finishing the step',
      heading: 'Save and finish',
      text: 'Printing and reports is marked Done by itself once the tray ticket format and what prints on a ticket are saved and tray tickets have a printer. On the last part, select Save and finish (it reads Finish this step until then).\n\nIf something is still missing, such as the tray ticket printer, it is listed and the step stays open. Otherwise Getting Started moves on to the next step.',
      media: {
        image: {
          src: '/help/printing-setup/finish.webp',
          width: 434,
          height: 1094,
        },
        clip: {
          src: '/help/printing-setup/finish.mp4',
          width: 434,
          height: 1094,
          durationSec: 2.6,
        },
        alt: 'The list of steps with Printing and reports Done, and Getting Started on the Selective Touch step.',
      },
    },
    {
      id: 'later',
      part: 'Printers, and finishing the step',
      heading: 'Changing it later',
      text: 'Every choice here is a facility setting. To change one later, use See all options in its part, or Facility Settings (Tray Tickets, Posting & Production, Labels, Print Agent): while Getting Started is on, those tabs also have Set up printing step by step, with a sample printout, which brings you back here.\n\nAdding printers, which printer prints what, and the times tray tickets print by themselves are on Printers and Print Schedules. See Printers and printing problems.',
      media: {
        image: {
          src: '/help/printing-setup/later.webp',
          width: 1280,
          height: 954,
        },
        alt: 'Facility Settings on the Tray Tickets tab, with the link Set up printing step by step, with a sample printout at the top.',
      },
    },
  ],
  tips: [
    'You can leave at any part. Save and come back later keeps what you have chosen, and the parts you have saved get a check mark.',
    'The sample printout uses made-up residents. Your printed tickets use your own residents and menu, so they may differ slightly.',
    'Each part with more settings has See all options, which opens the same settings in Facility Settings.',
  ],
  notes: [
    'The step is finished when the tray ticket format and what prints on a ticket are saved, and tray tickets have a printer through a print agent that is online. If your kitchen prints tray tickets from the desktop instead, tick We print from the desktop instead in part 7.',
    'The daily reports in part 1 are pinned as your own favorites. Everyone else can pin their own from the Reports page.',
    'Installing the print agent on a kitchen computer is done once, with Dietech support. Getting Started shows whether it is online.',
    'Getting Started is for administrators who can change the facility\'s settings. If you cannot open it, ask your administrator.',
  ],
  related: [
    'getting-started',
    'printers-and-printing',
    'tray-tickets',
    'favorites-and-print-queue',
    'running-a-report',
    'selective-touch-setup',
  ],
  recorded: '2026-11-03',
};

export default article;
